import React, {useRef, useEffect, useState} from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import type Player from 'video.js/dist/types/player';
import {ISubtitleTrack} from "../models/interfaces/subtitleTrack.interface.ts";
import {
  Box, Flex, IconButton, Slider, SliderTrack, SliderFilledTrack, SliderThumb,
  Menu, MenuButton, MenuList, MenuItem, Text
} from "@chakra-ui/react";
import {FaPlay, FaPause, FaVolumeUp, FaVolumeMute, FaExpand, FaCompress} from "react-icons/fa";
import {TbRewindBackward15, TbRewindForward15} from "react-icons/tb";
import {MdOutlineReplay5, MdOutlineForward5} from "react-icons/md";
import {BiCaptions} from "react-icons/bi";

const formatTime = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return [h, m, s].map(n => String(n).padStart(2, '0')).join(':');
};

interface VideoJSProps {
  options: Parameters<typeof videojs>[1];
  onReady?: (player: Player) => void;
  subtitles?: ISubtitleTrack[];
}

const VideoJS: React.FC<VideoJSProps> = ({options, onReady, subtitles}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<Player | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [subtitleTracks, setSubtitleTracks] = useState<TextTrack[]>([]);
  const [activeSubtitle, setActiveSubtitle] = useState<string | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      const videoElement = videoRef.current;

      if (videoElement && videoElement.offsetParent !== null && !playerRef.current) {
        clearInterval(interval);
        playerRef.current = videojs(videoElement, options, () => {
          if (onReady && playerRef.current) onReady(playerRef.current);
          const loadedTracks: TextTrack[] = [];
          subtitles?.forEach(({src, label, srclang, default: isDefault}) => {
            const trackElement = playerRef.current?.addRemoteTextTrack(
              {kind: 'subtitles', src, srclang, label, default: isDefault},
              false
            );
            const nativeTrack = (trackElement as unknown as { track: TextTrack }).track;
            if (nativeTrack) loadedTracks.push(nativeTrack);
          });
          setSubtitleTracks(loadedTracks);
          const active = loadedTracks.find(t => t.mode === 'showing');
          const defaultTrack = subtitles?.find(s => s.default)?.label;
          setActiveSubtitle(active?.label || defaultTrack || 'Off');
        });

        playerRef.current.on("play", () => setIsPlaying(true));
        playerRef.current.on("pause", () => setIsPlaying(false));
        playerRef.current.on("timeupdate", () => setCurrentTime(playerRef.current?.currentTime() ?? 0));
        playerRef.current.on("loadedmetadata", () => setDuration(playerRef.current?.duration() ?? 1));
        playerRef.current.on("volumechange", () => {
          setVolume(playerRef.current?.volume() ?? 1);
          setMuted(playerRef.current?.muted() ?? false);
        });
        playerRef.current.on("ratechange", () => {
          setPlaybackRate(playerRef.current?.playbackRate() ?? 1);
        });
      }
    }, 100);

    return () => {
      clearInterval(interval);
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [options, onReady, subtitles]);

  const handlePlaybackChange = (rate: number) => {
    if (!playerRef.current) return;
    playerRef.current.playbackRate(rate);
    setPlaybackRate(rate);
  };

  return (
    <Flex className="w-full h-full items-center flex-col mt-2">
      <Flex w="95%" position="relative" direction="column">
        {/* Video */}
        <Box
          data-vjs-player
          onClick={() => {
            if (document.fullscreenElement) return;
            if (playerRef.current?.paused()) {
              playerRef.current.play();
            } else {
              playerRef.current?.pause();
            }
          }}
          onDoubleClick={() => {
            if (isFullscreen) {
              document.exitFullscreen();
            } else {
              videoRef.current?.requestFullscreen();
            }
            setIsFullscreen(!isFullscreen);
          }}
        >
          <video
            ref={videoRef}
            className="video-js vjs-16-9 vjs-default-skin"
            playsInline
            preload="metadata"
            style={{width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px'}}
          />
        </Box>

        {/* Slider */}
        <Box w="full" position="relative">
          <Slider
            min={0}
            max={duration}
            step={1}
            value={currentTime}
            onChange={(val) => playerRef.current?.currentTime(Number(val))}
            w="full"
            position="absolute"
            top="-15px"
            left="0"
          >
            <SliderTrack bg="gray.700" h="5px">
              <SliderFilledTrack bg="purple.500"/>
            </SliderTrack>
            <SliderThumb boxSize="14px" bg="purple.500" border="2px solid white"/>
          </Slider>
        </Box>

        {/* Controls */}
        <Flex
          position="relative"
          w="full"
          bg="gray.800"
          p={2}
          borderBottomRadius="xl"
          align="center"
          top={"-25px"}
        >
          <Box position="absolute">
            <Text color="white" fontSize="sm">
              {formatTime(currentTime)} / {formatTime(duration)}
            </Text>
          </Box>

          <Flex className="w-full items-center justify-center gap-2">
            <IconButton
              aria-label="Rewind 15s"
              icon={<TbRewindBackward15/>}
              color="white"
              bg="transparent"
              onClick={() => playerRef.current?.currentTime(currentTime - 15)}
              _hover={{bg: "gray.700", opacity: 0.9}}
            />
            <IconButton
              aria-label="Rewind 5s"
              icon={<MdOutlineReplay5/>}
              color="white"
              bg="transparent"
              onClick={() => playerRef.current?.currentTime(currentTime - 5)}
              _hover={{bg: "gray.700", opacity: 0.9}}
            />
            <IconButton
              aria-label="Play/Pause"
              icon={isPlaying ? <FaPause/> : <FaPlay/>}
              color="white"
              bg="brandPurple.500"
              opacity="0.8"
              size="lg"
              borderRadius="full"
              onClick={() => playerRef.current?.paused() ? playerRef.current?.play() : playerRef.current?.pause()}
              _hover={{opacity: 1}}
            />
            <IconButton
              aria-label="Forward 5s"
              icon={<MdOutlineForward5/>}
              color="white"
              bg="transparent"
              onClick={() => playerRef.current?.currentTime(currentTime + 5)}
              _hover={{bg: "gray.700", opacity: 0.9}}
            />
            <IconButton
              aria-label="Forward 15s"
              icon={<TbRewindForward15/>}
              color="white"
              bg="transparent"
              onClick={() => playerRef.current?.currentTime(currentTime + 15)}
              _hover={{bg: "gray.700", opacity: 0.9}}
            />
          </Flex>

          <Box position="absolute" right="0">
            <Flex align="center" gap={2}>
              <Menu>
                <MenuButton
                  as={Box}
                  py={2}
                  px={3}
                  borderRadius="md"
                  cursor="pointer"
                  color="white"
                  _hover={{bg: "gray.700", opacity: 0.9}}
                >
                  <Flex
                    align="center"
                    fontSize="14px"
                  >
                    <Text>x</Text>
                    <Text>{playbackRate}</Text>
                  </Flex>
                </MenuButton>
                <MenuList
                  bg="gray.800"
                  border="1px solid"
                  borderColor="gray.700"
                  shadow="lg"
                  rounded="md"
                  minW="150px"
                  p={2}
                >
                  {[0.25, 0.5, 1, 1.25, 1.5, 2].map(rate => (
                    <MenuItem
                      key={rate}
                      bg={playbackRate == rate ? "brandPurple.500" : ""}
                      color="white"
                      onClick={() => handlePlaybackChange(rate)}
                      _hover={{bg: "brandPurple.500", opacity: 0.8}}
                    >
                      x{rate}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
              <Menu>
                <MenuButton
                  as={IconButton}
                  icon={<BiCaptions color="white"/>}
                  colorScheme="whiteAlpha"
                  variant="ghost"
                />
                <MenuList
                  bg="gray.800"
                  border="1px solid"
                  borderColor="gray.700"
                  shadow="lg"
                  rounded="md"
                  minW="150px"
                  p={2}
                >
                  <MenuItem
                    onClick={() => {
                      subtitleTracks.forEach(t => t.mode = 'disabled');
                      setActiveSubtitle('Off');
                    }}
                    color="white"
                    bg={activeSubtitle === 'Off' ? 'brandPurple.500' : ""}
                    _hover={{bg: "brandPurple.500", opacity:0.8}}
                  >
                    Off
                  </MenuItem>
                  {subtitleTracks.map((track, idx) => (
                    <MenuItem
                      key={idx}
                      onClick={() => {
                        subtitleTracks.forEach(t => t.mode = 'disabled');
                        track.mode = 'showing';
                        setActiveSubtitle(track.label);
                      }}
                      color="white"
                      bg={activeSubtitle === track.label ? 'brandPurple.500' : ""}
                      _hover={{bg: "brandPurple.500", opacity:0.8}}
                    >
                      {track.label || `Track ${idx + 1}`}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
              <Flex align="center">
                <IconButton
                  aria-label="Mute"
                  icon={muted ? <FaVolumeMute/> : <FaVolumeUp/>}
                  color="white"
                  bg="transparent"
                  onClick={() => {
                    const newMuted = !muted;
                    playerRef.current?.muted(newMuted);
                    setMuted(newMuted);
                  }}
                  _hover={{bg: "gray.700", opacity: 0.9}}
                />
                <Slider
                  w="40px"
                  min={0}
                  max={1}
                  step={0.05}
                  value={volume}
                  onChange={(val) => {
                    setVolume(val);
                    playerRef.current?.volume(val);
                  }}
                >
                  <SliderTrack bg="gray.700"><SliderFilledTrack bg="brandPurple.500"/></SliderTrack>
                  <SliderThumb boxSize={4} bg="white"/>
                </Slider>
              </Flex>
              <IconButton
                aria-label="Fullscreen"
                icon={isFullscreen ? <FaCompress/> : <FaExpand/>}
                color="white" bg="transparent"
                onClick={() => {
                  const fullscreen = !!document.fullscreenElement;
                  if (fullscreen) {
                    document.exitFullscreen().catch((err) => console.error(err));
                  } else {
                    videoRef.current?.requestFullscreen();
                  }
                  setIsFullscreen(!fullscreen);
                }}
                _hover={{bg: "gray.700", opacity: 0.9}}
              />
            </Flex>
          </Box>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default VideoJS;